<template>
  <div class="sin-wrap" @keydown.prevent="onKey" tabindex="0" ref="wrap">
    <!-- Score + state bar -->
    <div class="sin-bar">
      <span class="sin-score">{{ score }}</span>
      <span class="sin-state" :class="state">{{ stateLabel }}</span>
      <button class="sin-btn" @click.stop="togglePause" v-if="state !== 'dead'">
        {{ state === 'playing' ? '⏸' : '▶' }}
      </button>
      <button class="sin-btn" @click.stop="restart">↺</button>
    </div>

    <!-- Canvas -->
    <canvas ref="canvas" :width="size" :height="size" class="sin-canvas" @click.stop="focusWrap"></canvas>

    <!-- D-pad controls (for clicking, not keyboard typing) -->
    <div class="sin-dpad">
      <button class="sin-dpad-btn sin-up"   @click.stop="setDir('up')"   @mousedown.prevent>▲</button>
      <div class="sin-dpad-mid">
        <button class="sin-dpad-btn sin-left"  @click.stop="setDir('left')"  @mousedown.prevent>◀</button>
        <button class="sin-dpad-btn sin-right" @click.stop="setDir('right')" @mousedown.prevent>▶</button>
      </div>
      <button class="sin-dpad-btn sin-down"  @click.stop="setDir('down')"  @mousedown.prevent>▼</button>
    </div>
  </div>
</template>

<script>
const CELL = 11;
const COLS = 20;
const ROWS = 20;
const SIZE = CELL * COLS; // 220

export default {
  name: 'SnakeInNav',
  props: {
    paused: { type: Boolean, default: false }
  },
  data() {
    return {
      size: SIZE,
      snake: [],
      food: { x: 10, y: 10 },
      dir: 'right',
      nextDir: 'right',
      score: 0,
      state: 'idle', // idle | playing | paused | dead
      loop: null
    };
  },
  computed: {
    stateLabel() {
      return { idle: 'Ready', playing: '', paused: 'Paused', dead: 'Game Over' }[this.state] || '';
    }
  },
  watch: {
    paused(val) {
      if (val && this.state === 'playing') this.pauseGame();
      else if (!val && this.state === 'paused') this.resumeGame();
    }
  },
  mounted() {
    this.draw();
  },
  beforeDestroy() {
    clearInterval(this.loop);
  },
  methods: {
    focusWrap() { this.$refs.wrap && this.$refs.wrap.focus(); },

    restart() {
      clearInterval(this.loop);
      const mid = Math.floor(COLS / 2);
      this.snake = [
        { x: mid + 1, y: Math.floor(ROWS / 2) },
        { x: mid,     y: Math.floor(ROWS / 2) },
        { x: mid - 1, y: Math.floor(ROWS / 2) }
      ];
      this.dir = 'right';
      this.nextDir = 'right';
      this.score = 0;
      this.placeFood();
      this.state = 'playing';
      this.loop = setInterval(this.tick, 130);
      this.$nextTick(() => this.focusWrap());
    },

    togglePause() {
      if (this.state === 'playing') this.pauseGame();
      else if (this.state === 'paused') this.resumeGame();
      else this.restart();
    },

    pauseGame() {
      clearInterval(this.loop);
      this.loop = null;
      this.state = 'paused';
      this.draw();
    },

    resumeGame() {
      if (this.paused) return; // parent still wants pause
      this.state = 'playing';
      this.loop = setInterval(this.tick, 130);
      this.$nextTick(() => this.focusWrap());
    },

    placeFood() {
      let pos;
      do {
        pos = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) };
      } while (this.snake.some(s => s.x === pos.x && s.y === pos.y));
      this.food = pos;
    },

    tick() {
      this.dir = this.nextDir;
      const head = { ...this.snake[0] };
      if (this.dir === 'right') head.x++;
      else if (this.dir === 'left')  head.x--;
      else if (this.dir === 'up')    head.y--;
      else if (this.dir === 'down')  head.y++;

      // Wall collision
      if (head.x < 0 || head.x >= COLS || head.y < 0 || head.y >= ROWS) { this.die(); return; }
      // Self collision
      if (this.snake.some(s => s.x === head.x && s.y === head.y)) { this.die(); return; }

      this.snake.unshift(head);
      if (head.x === this.food.x && head.y === this.food.y) {
        this.score++;
        this.$emit('score-update', this.score);
        this.placeFood();
      } else {
        this.snake.pop();
      }
      this.draw();
    },

    die() {
      clearInterval(this.loop);
      this.loop = null;
      this.state = 'dead';
      this.$emit('game-over', this.score);
      this.drawDead();
    },

    setDir(d) {
      const opp = { right: 'left', left: 'right', up: 'down', down: 'up' };
      if (opp[d] !== this.dir) this.nextDir = d;
      if (this.state === 'idle') this.restart();
      else if (this.state === 'paused') this.resumeGame();
    },

    onKey(e) {
      const map = { ArrowRight:'right', ArrowLeft:'left', ArrowUp:'up', ArrowDown:'down',
                    KeyD:'right', KeyA:'left', KeyW:'up', KeyS:'down' };
      if (e.code === 'Space') { e.preventDefault(); this.togglePause(); return; }
      const d = map[e.code] || map[e.key];
      if (d) { e.preventDefault(); this.setDir(d); }
    },

    draw() {
      const cv = this.$refs.canvas;
      if (!cv) return;
      const ctx = cv.getContext('2d');
      // Background
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, SIZE, SIZE);

      // Grid dots (subtle)
      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      for (let x = 0; x < COLS; x++) {
        for (let y = 0; y < ROWS; y++) {
          ctx.fillRect(x * CELL + CELL/2 - 1, y * CELL + CELL/2 - 1, 2, 2);
        }
      }

      if (this.state === 'idle') {
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = 'bold 13px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('Tap ▶ or arrow to start', SIZE/2, SIZE/2);
        return;
      }

      // Food (purple glow)
      const fx = this.food.x * CELL + CELL/2;
      const fy = this.food.y * CELL + CELL/2;
      const grd = ctx.createRadialGradient(fx, fy, 1, fx, fy, CELL);
      grd.addColorStop(0, '#c084fc');
      grd.addColorStop(1, 'rgba(168,85,247,0)');
      ctx.fillStyle = grd;
      ctx.beginPath(); ctx.arc(fx, fy, CELL, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = '#a855f7';
      ctx.beginPath(); ctx.arc(fx, fy, CELL/2 - 1, 0, Math.PI*2); ctx.fill();

      // Snake
      this.snake.forEach((seg, i) => {
        const isHead = i === 0;
        const alpha = isHead ? 1 : Math.max(0.4, 1 - i * 0.04);
        ctx.fillStyle = isHead ? `rgba(34,197,94,${alpha})` : `rgba(22,163,74,${alpha})`;
        const pad = isHead ? 0 : 1;
        ctx.beginPath();
        ctx.roundRect
          ? ctx.roundRect(seg.x * CELL + pad, seg.y * CELL + pad, CELL - pad*2, CELL - pad*2, isHead ? 3 : 2)
          : ctx.rect(seg.x * CELL + pad, seg.y * CELL + pad, CELL - pad*2, CELL - pad*2);
        ctx.fill();
      });

      // Paused overlay
      if (this.state === 'paused') {
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(0, 0, SIZE, SIZE);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 18px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('PAUSED', SIZE/2, SIZE/2);
      }
    },

    drawDead() {
      this.draw();
      const cv = this.$refs.canvas;
      if (!cv) return;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = 'rgba(0,0,0,0.65)';
      ctx.fillRect(0, 0, SIZE, SIZE);
      ctx.fillStyle = '#f87171';
      ctx.font = 'bold 16px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('GAME OVER', SIZE/2, SIZE/2 - 10);
      ctx.fillStyle = '#fff';
      ctx.font = '13px system-ui';
      ctx.fillText('Score: ' + this.score, SIZE/2, SIZE/2 + 12);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '11px system-ui';
      ctx.fillText('Tap ↺ to restart', SIZE/2, SIZE/2 + 32);
    }
  }
};
</script>

<style scoped>
.sin-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  outline: none;
  padding: 8px 0 4px;
}
.sin-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 220px;
}
.sin-score {
  font-size: 13px;
  font-weight: 700;
  color: #22c55e;
  flex: 1;
}
.sin-state {
  font-size: 10px;
  color: rgba(255,255,255,0.4);
}
.sin-state.dead { color: #f87171; }
.sin-btn {
  background: rgba(255,255,255,0.08);
  border: none;
  color: rgba(255,255,255,0.7);
  border-radius: 4px;
  width: 24px; height: 24px;
  font-size: 12px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .12s;
}
.sin-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.sin-canvas {
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.07);
  cursor: pointer;
  display: block;
}
.sin-dpad {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
}
.sin-dpad-mid { display: flex; gap: 28px; }
.sin-dpad-btn {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.6);
  border-radius: 5px;
  width: 28px; height: 28px;
  font-size: 11px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .1s;
  user-select: none;
  -webkit-user-select: none;
}
.sin-dpad-btn:active, .sin-dpad-btn:hover {
  background: rgba(255,255,255,0.15);
  color: #fff;
}
</style>
